import axios from "axios";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { ProductEditSchema } from "../../../ValidationSchema";
import ReactSelect from "../../../Components/PortalSelect";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import AddOptionModal from "./AddOptionModal";
import { alSelectStyles } from "../../../styles/reactSelectTheme";

const colourStyles = alSelectStyles;

const ProductEdit = () => {
  const Navigate = useNavigate();
  const location = useLocation();
  const productId = location?.state?.id;

  const [formState, setFormState] = useState({
    categoryData: [],
    subCategoryData: [],
    allSubCategoryData: [],
    extraFields: [{ label: "", inputValue: "", values: [] }],
    mediaPreviews: [],
    showAddOptionModal: false,
    addOptionModalField: null,
    isLoading: false,
    validationErrors: {},
    faqs: [{ question: "", answer: "" }],
    existingImages: [],
  });

  // All fields now follow the same pattern
  const [sizeData, setSizeData] = useState([]);
  const [materialColorData, setMaterialColorData] = useState([
    { label: "White Duplex", value: "White Duplex" },
    { label: "Brown Kraft", value: "Brown Kraft" },
    { label: "Art Paper", value: "Art Paper" },
  ]);
  const [printCoverageData, setPrintCoverageData] = useState([
    { label: "Single", value: "Single" },
    { label: "Multi", value: "Multi" },
    { label: "No print", value: "No print" },
  ]);
  const [paperGsmData, setPaperGsmData] = useState([
    { label: "100gsm", value: "100gsm" },
    { label: "120gsm", value: "120gsm" },
    { label: "150gsm", value: "150gsm" },
    { label: "180gsm", value: "180gsm" },
    { label: "220gsm", value: "220gsm" },
    { label: "above", value: "above" },
  ]);
  const [burstFactorData, setBurstFactorData] = useState([
    { label: "18", value: "18" },
    { label: "20", value: "20" },
    { label: "22", value: "22" },
    { label: "24", value: "24" },
    { label: "28", value: "28" },
    { label: "35", value: "35" },
    { label: "40", value: "40" },
  ]);
  const [noOfPlyData, setNoOfPlyData] = useState([
    { label: "1Ply", value: "1Ply" },
    { label: "2Ply", value: "2Ply" },
    { label: "5Ply", value: "5Ply" },
  ]);

  const initialValues = {
    title: "",
    size: [],
    material_color: [],
    print_coverage: [],
    paper_gsm: [],
    tags: "",
    slug: "",
    category: "",
    sub_category: "",
    description: "",
    product_details: "",
    date_time: "",
    burst_factor: [],
    no_of_ply: [],
    product_image: [],
    gallery_image_path: "",
    lamination: "",
    quantity: "",
  };

  const updateFormState = (updates) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  };

  // FAQ Functions
  const handleFaqChange = (index, field, value) => {
    const updatedFaqs = [...formState.faqs];
    updatedFaqs[index][field] = value;
    updateFormState({ faqs: updatedFaqs });
  };

  const addFaq = () => {
    updateFormState({
      faqs: [...formState.faqs, { question: "", answer: "" }],
    });
  };

  const removeFaq = (index) => {
    if (formState.faqs.length <= 1) return;
    const updatedFaqs = [...formState.faqs];
    updatedFaqs.splice(index, 1);
    updateFormState({ faqs: updatedFaqs });
  };

  // Unified function to handle adding options to all fields
  const handleAddOption = (field, newOption) => {
    switch (field) {
      case "Size":
        setSizeData((prev) => [...prev, newOption]);
        EditProduct.setFieldValue("size", [
          ...EditProduct.values.size,
          newOption.value,
        ]);
        break;
      case "MaterialColor":
        setMaterialColorData((prev) => [...prev, newOption]);
        EditProduct.setFieldValue("material_color", [
          ...EditProduct.values.material_color,
          newOption.value,
        ]);
        break;
      case "PrintCoverage":
        setPrintCoverageData((prev) => [...prev, newOption]);
        EditProduct.setFieldValue("print_coverage", [
          ...EditProduct.values.print_coverage,
          newOption.value,
        ]);
        break;
      case "PaperGsm":
        setPaperGsmData((prev) => [...prev, newOption]);
        EditProduct.setFieldValue("paper_gsm", [
          ...EditProduct.values.paper_gsm,
          newOption.value,
        ]);
        break;
      case "BurstFactor":
        setBurstFactorData((prev) => [...prev, newOption]);
        EditProduct.setFieldValue("burst_factor", [
          ...EditProduct.values.burst_factor,
          newOption.value,
        ]);
        break;
      case "NoOfPly":
        setNoOfPlyData((prev) => [...prev, newOption]);
        EditProduct.setFieldValue("no_of_ply", [
          ...EditProduct.values.no_of_ply,
          newOption.value,
        ]);
        break;
      default:
        break;
    }
  };

  const openAddOptionModal = (field) => {
    updateFormState({
      showAddOptionModal: true,
      addOptionModalField: field,
    });
  };

  const handleInputChange = (index, field, value) => {
    const updatedFields = [...formState.extraFields];
    updatedFields[index][field] = value;
    updateFormState({ extraFields: updatedFields });
  };

  const handleAddRow = () => {
    updateFormState({
      extraFields: [
        ...formState.extraFields,
        { label: "", inputValue: "", values: [] },
      ],
    });
  };

  const handleRemoveRow = (index) => {
    if (formState.extraFields.length <= 1) return;
    const updatedFields = [...formState.extraFields];
    updatedFields.splice(index, 1);
    updateFormState({ extraFields: updatedFields });
  };

  const handleAddValue = (e, index) => {
    e.preventDefault();
    const trimmed = formState.extraFields[index].inputValue.trim();
    if (trimmed === "") return;

    const updatedFields = [...formState.extraFields];
    updatedFields[index].values.push(trimmed);
    updatedFields[index].inputValue = "";
    updateFormState({ extraFields: updatedFields });
  };

  const handleRemoveValue = (rowIndex, valIndex) => {
    const updatedFields = [...formState.extraFields];
    updatedFields[rowIndex].values.splice(valIndex, 1);
    updateFormState({ extraFields: updatedFields });
  };

  const CategoriesData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/category`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        }
      );

      const categories =
        res?.data?.data?.categories?.map((item) => ({
          label: item?.product_category_name || "Untitled",
          value: item?.product_category_id,
        })) || [];

      updateFormState({ categoryData: categories });
    } catch (err) {
      console.error("Error fetching categories:", err);
      toast.error("Failed to load categories");
    }
  };

  // const SizeData = async () => {
  //   try {
  //     const res = await axios.get(`${process.env.REACT_APP_URL}/size`);

  //     const sizes =
  //       res?.data?.data?.map((item) => ({
  //         label: item?.size || "Untitled",
  //         value: item?.size,
  //       })) || [];

  //     setSizeData(sizes);
  //   } catch (err) {
  //     console.error("Error fetching sizes:", err);
  //     if (err.response?.data?.message === "Unauthorized!") {
  //       Navigate("/");
  //     } else {
  //       toast.error("Failed to load sizes");
  //     }
  //   }
  // };

  const SubCategoriesData = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/sub_category`,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("Token")}` },
        }
      );

      const subCategories =
        res?.data?.data?.subcategories?.map((item) => ({
          label: item?.product_subcategory_name || "Untitled",
          value: item?.product_subcategory_id,
          categoryId: item?.product_category_id,
        })) || [];

      updateFormState({
        subCategoryData: subCategories,
        allSubCategoryData: subCategories,
      });
    } catch (err) {
      console.error("Error fetching sub-categories:", err);
      toast.error("Failed to load sub-categories");
    }
  };

  const fetchProductData = async () => {
    if (!productId) return;

    try {
      updateFormState({ isLoading: true });
      const res = await axios.get(
        `${process.env.REACT_APP_URL}/admin/products/${productId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("Token")}`,
          },
        }
      );

      const data = res.data.data;

      // Helper function to transform string values to arrays
      const transformToArray = (fieldValue) => {
        if (Array.isArray(fieldValue)) return fieldValue;
        if (typeof fieldValue === "string") {
          return fieldValue
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item !== "");
        }
        return [];
      };

      // Special function for size field to handle the "12X12X12" format
      const transformSizeField = (sizeValue) => {
        if (Array.isArray(sizeValue)) return sizeValue;
        if (typeof sizeValue === "string") {
          // Split by comma and trim each size
          return sizeValue
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item !== "");
        }
        return [];
      };

      // Set form values
      EditProduct.setValues({
        title: data.title || "",
        size: transformSizeField(data.size),
        material_color: transformToArray(data.material_color),
        print_coverage: transformToArray(data.print_coverage),
        paper_gsm: transformToArray(data.paper_gsm),
        tags: data.tags || "",
        slug: data.slug || "",
        category: data.category || "",
        sub_category: data.sub_category || "",
        description: data.description || "",
        product_details: data.product_details || "",
        date_time: data.date_time || "",
        burst_factor: transformToArray(data.burst_factor),
        no_of_ply: transformToArray(data.no_of_ply),
        product_image: [],
        gallery_image_path: data.gallery_image_path || "",
        lamination: data.lamination || "",
        quantity: data.quantity || "",
      });

      // Set existing images for preview
      if (data.product_image) {
        let imageArray = [];

        try {
          // Try to parse if backend sent JSON string
          const parsed = JSON.parse(data.product_image);
          if (Array.isArray(parsed)) {
            imageArray = parsed;
          } else if (typeof parsed === "string") {
            imageArray = [parsed];
          }
        } catch {
          // Fallback if plain string or comma-separated
          if (typeof data.product_image === "string") {
            imageArray = data.product_image
              .replace(/^\[|\]$/g, "")
              .split(",")
              .map((s) => s.replace(/['"]/g, "").trim())
              .filter(Boolean);
          } else if (Array.isArray(data.product_image)) {
            imageArray = data.product_image;
          }
        }

        updateFormState({
          existingImages: imageArray,
          mediaPreviews: imageArray.map((imgPath) => {
            // Ensure we don’t duplicate /uploads/
            const cleanPath = imgPath.startsWith("/uploads/")
              ? imgPath
              : `/uploads/${imgPath}`;
            return {
              url: `${process.env.REACT_APP_IMAGE_URL}${cleanPath}`,
              type: "image",
              name: cleanPath.replace("/uploads/products/", ""),
              // The stored path, which is what `existingImages` holds and what
              // the API expects back. Matching on `name` (a bare filename)
              // never matched, so removing an image did nothing.
              path: cleanPath,
              isExisting: true,
            };
          }),
        });
      }

      // Pre-select the values in the ReactSelect components
      // This needs to be done after a small delay to ensure the form values are set
      setTimeout(() => {
        // Pre-select size values
        if (data.size) {
          const sizeValues = transformSizeField(data.size);
          const selectedSizeOptions = sizeData.filter((option) =>
            sizeValues.includes(option.value)
          );
          // This will automatically update the ReactSelect display
        }

        // Pre-select other field values similarly
        const preSelectField = (fieldName, optionsData) => {
          const fieldValues = transformToArray(data[fieldName]);
          return optionsData.filter((option) =>
            fieldValues.includes(option.value)
          );
        };

        // The ReactSelect components will automatically show selected values
        // because we're using the value prop with the filtered options
      }, 100);

      // Set additional fields. The API returns these as `additionalFields`;
      // reading `additional_fields` always came back undefined, so saved
      // extra fields never showed up when editing a product.
      const additionalFieldsData = data.additionalFields || data.additional_fields;
      if (additionalFieldsData) {
        try {
          let extraFieldsData = additionalFieldsData;

          if (typeof extraFieldsData === "string") {
            extraFieldsData = JSON.parse(extraFieldsData);
          }

          if (Array.isArray(extraFieldsData)) {
            const formattedExtraFields = extraFieldsData.map((field) => {
              let parsedValues = field.value;

              if (typeof parsedValues === "string") {
                try {
                  parsedValues = JSON.parse(parsedValues);
                } catch {
                  parsedValues = [parsedValues];
                }
              }

              return {
                label: field.label || "",
                inputValue: "",
                values: Array.isArray(parsedValues)
                  ? parsedValues
                  : parsedValues
                  ? [parsedValues]
                  : [],
              };
            });

            updateFormState({ extraFields: formattedExtraFields });
          } else if (
            typeof extraFieldsData === "object" &&
            Object.keys(extraFieldsData).length > 0
          ) {
            const formattedExtraFields = Object.entries(extraFieldsData).map(
              ([label, values]) => ({
                label,
                inputValue: "",
                values:
                  typeof values === "string"
                    ? (() => {
                        try {
                          return JSON.parse(values);
                        } catch {
                          return [values];
                        }
                      })()
                    : Array.isArray(values)
                    ? values
                    : [values],
              })
            );

            updateFormState({ extraFields: formattedExtraFields });
          }
        } catch (e) {
          console.error("Error parsing extra fields:", e);
          updateFormState({ extraFields: [] });
        }
      }

      // Set FAQs if available
      if (data.faqs) {
        try {
          const faqsData =
            typeof data.faqs === "string" ? JSON.parse(data.faqs) : data.faqs;
          if (Array.isArray(faqsData) && faqsData.length > 0) {
            updateFormState({ faqs: faqsData });
          }
        } catch (e) {
          console.error("Error parsing FAQs:", e);
        }
      }
    } catch (err) {
      console.error("Error fetching product data:", err);
      if (err.response?.data?.message === "Unauthorized!") {
        Navigate("/");
      } else {
        toast.error("Failed to load product data");
      }
    } finally {
      updateFormState({ isLoading: false });
    }
  };

  const EditProduct = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: ProductEditSchema,
    onSubmit: async (values, action) => {
      const errors = {};
      if (!values.title) errors.title = "Product name is required";
      if (!values.category) errors.category = "Category is required";

      if (Object.keys(errors).length > 0) {
        updateFormState({ validationErrors: errors });
        const firstErrorField = Object.keys(errors)[0];
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }

      updateFormState({ validationErrors: {}, isLoading: true });

      try {
        const formData = new FormData();
        formData.append("id", productId);
        formData.append("title", values.title);

        // Append array fields correctly - all fields now use the same format
        const arrayFields = [
          "size",
          "material_color",
          "print_coverage",
          "paper_gsm",
          "burst_factor",
          "no_of_ply",
        ];

        arrayFields.forEach((field) => {
          if (values[field] && values[field].length) {
            // Special handling for size field to maintain existing format
            if (field === "size") {
              const formatSize = (size) =>
                Array.isArray(size) ? size.join("x") : size;
              if (values.size.length > 1) {
                formData.append("size", values.size.map(formatSize).join(", "));
              } else {
                formData.append("size", formatSize(values.size[0]));
              }
            } else {
              formData.append(field, values[field].join(", "));
            }
          }
        });

        // Skip blanks rather than posting the string "undefined"/"" for them.
        const appendIfSet = (key, value) => {
          if (value === undefined || value === null || value === "") return;
          formData.append(key, value);
        };

        appendIfSet("tags", values.tags);
        appendIfSet("slug", values.slug);
        formData.append("category", values.category);
        // Sent even when blank so clearing the sub-category is persisted.
        formData.append("sub_category", values.sub_category || "");
        formData.append("description", values.description);
        appendIfSet("product_details", values.product_details);
        appendIfSet("lamination", values.lamination);
        appendIfSet("quantity", values.quantity);

        // Append FAQs as JSON
        formData.append(
          "faqs",
          JSON.stringify(
            formState.faqs.filter((faq) => faq.question && faq.answer)
          )
        );

        // Append new files only
        values.product_image.forEach((file) => {
          formData.append("product_image", file);
        });

        // The paths kept in the editor. Always sent — an empty list is a
        // meaningful instruction to drop every existing image.
        formData.append(
          "existing_images",
          JSON.stringify(formState.existingImages)
        );

        // Extra fields were posted as `extra_fields[label]`, which the API
        // does not read, so edits to them were silently dropped. Post the
        // same `additional_fields` JSON the add form uses.
        formData.append(
          "additional_fields",
          JSON.stringify(
            formState.extraFields
              .filter((f) => f.label && f.values.length > 0)
              .map((f) => ({ label: f.label, value: f.values.join(", ") }))
          )
        );

        const response = await axios.put(
          `${process.env.REACT_APP_URL}/admin/products/${productId}`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("Token")}`,
              "Content-Type": "multipart/form-data",
            },
            timeout: 30000,
          }
        );

        if (response.status === 200) {
          // The API returns `message`; `msg` was always undefined, so the list
          // page showed no confirmation after a successful edit.
          Navigate("/admin/product", {
            state: response.data?.message || "Product updated successfully",
          });
        }
      } catch (err) {
        console.error("Error updating product:", err);

        if (err.response) {
          toast.error(
            err.response.data?.message ||
              err.response.data?.message ||
              `Server error: ${err.response.status}`
          );
        } else if (err.request) {
          toast.error("No response from server. Please check your connection.");
        } else {
          toast.error("An error occurred while setting up the request.");
        }

        if (err.response?.status === 401 || err.response?.status === 403) {
          localStorage.clear();
          Navigate("/admin/login");
        }
      } finally {
        updateFormState({ isLoading: false });
        action.setSubmitting(false);
      }
    },
  });

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const validFiles = selectedFiles.filter((file) => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    const previews = validFiles.map((file) => ({
      url: URL.createObjectURL(file),
      type: "image",
      name: file.name,
      file,
      isExisting: false,
    }));

    const updatedPreviews = [...formState.mediaPreviews, ...previews];
    const updatedFiles = [...EditProduct.values.product_image, ...validFiles];

    updateFormState({ mediaPreviews: updatedPreviews });
    EditProduct.setFieldValue("product_image", updatedFiles);

    if (formState.validationErrors.product_image) {
      updateFormState((prev) => ({
        ...prev,
        validationErrors: { ...prev.validationErrors, product_image: null },
      }));
    }
  };

  const removeImage = (index) => {
    const updatedPreviews = [...formState.mediaPreviews];
    const previewToRemove = updatedPreviews[index];

    if (previewToRemove.isExisting) {
      // Remove from existing images
      const updatedExisting = formState.existingImages.filter(
        (img) => img !== previewToRemove.path
      );
      updateFormState({ existingImages: updatedExisting });
    } else {
      // Remove from new files
      const updatedFiles = [...EditProduct.values.product_image];
      const fileIndex = updatedFiles.findIndex(
        (file) => file.name === previewToRemove.name
      );
      if (fileIndex !== -1) {
        updatedFiles.splice(fileIndex, 1);
        EditProduct.setFieldValue("product_image", updatedFiles);
      }
    }

    // Revoke object URL for new images
    if (!previewToRemove.isExisting) {
      URL.revokeObjectURL(previewToRemove.url);
    }

    updatedPreviews.splice(index, 1);
    updateFormState({ mediaPreviews: updatedPreviews });
  };

  useEffect(() => {
    CategoriesData();
    SubCategoriesData();
    // SizeData();
    if (productId) {
      fetchProductData();
    }

    return () => {
      formState.mediaPreviews.forEach((preview) => {
        if (!preview.isExisting) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, [productId]);

  const renderError = (fieldName) => {
    const error =
      EditProduct.errors[fieldName] && EditProduct.touched[fieldName]
        ? EditProduct.errors[fieldName]
        : formState.validationErrors[fieldName];

    return error ? (
      <div className="text-danger mt-2 ml-1 validation-message">{error}</div>
    ) : null;
  };

  return (
    <div className="al">
      <Toaster position="top-center" reverseOrder={false} />

      <div className="al-head">
        <div>
          <h1 className="al-title">Edit Product</h1>
          <p className="al-sub">Update this product's specifications, images and FAQs.</p>
        </div>
        <div className="al-head__actions">
          <button type="button" className="al-btn" onClick={() => Navigate(-1)} disabled={formState.isLoading}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
            Back
          </button>
        </div>
      </div>

      <div className="al-card">
        <div className="al-cardhead">
          <div className="al-cardhead__icon"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.59 13.41 13.42 20.6a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" /><line x1="7" y1="7" x2="7.01" y2="7" /></svg></div>
          <h3 className="al-cardhead__title">Product Details</h3>
        </div>
            <form onSubmit={EditProduct.handleSubmit}>
              {/* Plain wrapper — the legacy `.form-group` class pulled in
                  structure.css rules (`.form-group input` / `.form-group label`)
                  that outrank `.al-input`/`.al-label`, so this form's inputs and
                  labels rendered with the wrong size, radius and focus ring,
                  unlike every other admin form. The `.row`/`.col` grid handles
                  layout on its own. */}
              <div>
                <div className="row">
                  {/* Product Name */}
                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Product Name <span className="text-danger">*</span>
                    </label>
                    <input
                      className="al-input"
                      type="text"
                      placeholder="Enter Product Name"
                      value={EditProduct.values.title}
                      onChange={EditProduct.handleChange}
                      onBlur={EditProduct.handleBlur}
                      name="title"
                      disabled={formState.isLoading}
                    />
                    {renderError("title")}
                  </div>

                  {/* Category Field */}
                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Category <span className="text-danger">*</span>
                    </label>
                    <ReactSelect
                      options={formState.categoryData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Category"
                      name="category"
                      onChange={(e) => {
                        EditProduct.setFieldValue("category", e?.value);

                        if (e?.value) {
                          const filtered = formState.allSubCategoryData.filter(
                            (sub) => sub.categoryId === e.value
                          );
                          updateFormState({
                            subCategoryData: filtered,
                          });
                        } else {
                          updateFormState({
                            subCategoryData: [],
                          });
                          EditProduct.setFieldValue("sub_category", null);
                        }

                        if (formState.validationErrors.category) {
                          updateFormState((prev) => ({
                            ...prev,
                            validationErrors: {
                              ...prev.validationErrors,
                              category: null,
                            },
                          }));
                        }
                      }}
                      value={
                        formState.categoryData.find(
                          (item) => item.value === EditProduct.values.category
                        ) || null
                      }
                      isDisabled={formState.isLoading}
                    />
                    {renderError("category")}
                  </div>

                  {/* Sub-Category Field */}
                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Sub-Category
                    </label>
                    <ReactSelect
                      options={formState.subCategoryData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Sub-Category"
                      name="sub_category"
                      onChange={(e) => {
                        EditProduct.setFieldValue("sub_category", e?.value);
                      }}
                      value={
                        formState.subCategoryData.find(
                          (item) =>
                            item.value === EditProduct.values.sub_category
                        ) || null
                      }
                      isDisabled={
                        formState.isLoading || !EditProduct.values.category
                      }
                    />
                  </div>

                  {/* Size Field - Now consistent with other fields */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      Size
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("Size")}
                        title="Add New Size"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={sizeData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Size"
                      name="size"
                      onChange={(selectedOptions) =>
                        EditProduct.setFieldValue(
                          "size",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={sizeData.filter((option) =>
                        EditProduct.values.size?.includes(option.value)
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("size")}
                  </div>

                  {/* Material Color Field */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      Material Color
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("MaterialColor")}
                        title="Add New Material Color"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={materialColorData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Material Color"
                      name="material_color"
                      onChange={(selectedOptions) =>
                        EditProduct.setFieldValue(
                          "material_color",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={materialColorData.filter((option) =>
                        EditProduct.values.material_color?.includes(
                          option.value
                        )
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("material_color")}
                  </div>

                  {/* Print Coverage Field */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      Print Coverage
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("PrintCoverage")}
                        title="Add New Print Coverage"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={printCoverageData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Print Coverage"
                      name="print_coverage"
                      onChange={(selectedOptions) =>
                        EditProduct.setFieldValue(
                          "print_coverage",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={printCoverageData.filter((option) =>
                        EditProduct.values.print_coverage?.includes(
                          option.value
                        )
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("print_coverage")}
                  </div>

                  {/* Paper GSM Field */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      Paper GSM
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("PaperGsm")}
                        title="Add New Paper GSM"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={paperGsmData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Paper GSM"
                      name="paper_gsm"
                      onChange={(selectedOptions) =>
                        EditProduct.setFieldValue(
                          "paper_gsm",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={paperGsmData.filter((option) =>
                        EditProduct.values.paper_gsm?.includes(option.value)
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("paper_gsm")}
                  </div>

                  {/* Burst Factor Field */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      Burst Factor
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("BurstFactor")}
                        title="Add New Burst Factor"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={burstFactorData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Burst Factor"
                      name="burst_factor"
                      onChange={(selectedOptions) =>
                        EditProduct.setFieldValue(
                          "burst_factor",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={burstFactorData.filter((option) =>
                        EditProduct.values.burst_factor?.includes(option.value)
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("burst_factor")}
                  </div>

                  {/* No Of Ply Field */}
                  <div className="col-md-6 mb-3">
                    <label
                      className="al-label"
                      style={{ display: "flex", justifyContent: "space-between" }}
                    >
                      No Of Ply
                      <span
                        className="al-addopt"
                        onClick={() => openAddOptionModal("NoOfPly")}
                        title="Add New No Of Ply"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                        New
                      </span>
                    </label>
                    <ReactSelect
                      isMulti
                      options={noOfPlyData}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Ply"
                      name="no_of_ply"
                      onChange={(selectedOptions) =>
                        EditProduct.setFieldValue(
                          "no_of_ply",
                          selectedOptions?.map((option) => option.value) || []
                        )
                      }
                      value={noOfPlyData.filter((option) =>
                        EditProduct.values.no_of_ply?.includes(option.value)
                      )}
                      isDisabled={formState.isLoading}
                    />
                    {renderError("no_of_ply")}
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Lamination
                    </label>
                    <ReactSelect
                      options={[
                        { label: "Yes", value: "Yes" },
                        { label: "No", value: "No" },
                      ]}
                      isClearable={false}
                      styles={colourStyles}
                      menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                      menuPosition="fixed"
                      components={{
                        DropdownIndicator: () => null,
                        IndicatorSeparator: () => null,
                      }}
                      placeholder="Select Lamination"
                      name="lamination"
                      onChange={(e) =>
                        EditProduct.setFieldValue("lamination", e?.value)
                      }
                      value={
                        EditProduct.values.lamination
                          ? {
                              label: EditProduct.values.lamination,
                              value: EditProduct.values.lamination,
                            }
                          : null
                      }
                    />
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Quantity
                    </label>
                    <input
                      className="al-input"
                      type="text"
                      placeholder="Enter Quantity"
                      value={EditProduct.values.quantity}
                      onChange={EditProduct.handleChange}
                      onBlur={EditProduct.handleBlur}
                      name="quantity"
                    />
                  </div>

                  {/* Product Image Field */}
                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Product Images
                    </label>
                    <input
                      className="al-input al-input--file"
                      type="file"
                      multiple
                      accept="image/*"
                      onChange={handleFileChange}
                      onBlur={EditProduct.handleBlur}
                      name="product_image"
                      disabled={formState.isLoading}
                    />

                    {/* Image Previews */}
                    <div className="mt-3">
                      <div className="d-flex flex-wrap gap-2">
                        {formState.mediaPreviews.map((preview, index) => (
                          <div
                            key={index}
                            className="position-relative"
                            style={{ width: "100px", height: "100px" }}
                          >
                            <img
                              src={preview.url}
                              alt={`Preview ${index + 1}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "8px",
                                border: "1px solid var(--color-border)",
                              }}
                            />
                            <button
                              type="button"
                              className="al-imgx"
                              onClick={() => removeImage(index)}
                              disabled={formState.isLoading}
                            >
                              &times;
                            </button>
                            {preview.isExisting && (
                              <span
                                className="al-pill"
                                style={{
                                  position: "absolute",
                                  bottom: "4px",
                                  left: "4px",
                                  fontSize: "9.5px",
                                  padding: "1px 7px",
                                  background: "var(--color-info-bg)",
                                  color: "var(--color-info-text)",
                                }}
                              >
                                Existing
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                      {formState.mediaPreviews.length > 0 && (
                        <small className="text-muted">
                          {formState.mediaPreviews.length} image(s)
                        </small>
                      )}
                    </div>
                  </div>

                  <div className="col-md-6 mb-3">
                    <label className="al-label">
                      Tags
                    </label>
                    <input
                      className="al-input"
                      type="text"
                      placeholder="Enter Tags (comma separated)"
                      value={EditProduct.values.tags}
                      onChange={(e) =>
                        EditProduct.setFieldValue("tags", e.target.value)
                      }
                      onBlur={EditProduct.handleBlur}
                      name="tags"
                    />
                  </div>
                </div>

                {/* Product Details Field */}
                <div className="mb-4">
                  <label className="al-label">
                    Product Details
                  </label>
                  <textarea
                    className="al-input"
                    rows="5"
                    placeholder="Enter product details"
                    value={EditProduct.values.product_details}
                    onChange={EditProduct.handleChange}
                    onBlur={EditProduct.handleBlur}
                    name="product_details"
                    disabled={formState.isLoading}
                  ></textarea>
                </div>

                {/* Additional Fields Section */}
                <div className="al-subpanel">
                  <div className="al-subpanel__head">
                    <span className="al-subpanel__title">Additional Fields</span>
                    <button
                      type="button"
                      className="al-btn al-btn--sm"
                      onClick={handleAddRow}
                      disabled={formState.isLoading}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      Add Field
                    </button>
                  </div>

                  {formState.extraFields.map((field, index) => (
                    <div className="al-fieldrow" key={index}>
                      <div className="al-fieldrow__main">
                        <input
                          type="text"
                          className="al-input al-fieldrow__label"
                          placeholder="Field label"
                          value={field.label}
                          onChange={(e) =>
                            handleInputChange(index, "label", e.target.value)
                          }
                          disabled={formState.isLoading}
                        />

                        <div className="al-fieldrow__value">
                          <input
                            type="text"
                            className="al-input"
                            placeholder="Enter value"
                            value={field.inputValue}
                            onChange={(e) =>
                              handleInputChange(
                                index,
                                "inputValue",
                                e.target.value
                              )
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddValue(e, index);
                              }
                            }}
                            disabled={formState.isLoading}
                          />
                          <button
                            type="button"
                            className="al-btn al-btn--primary al-btn--sm"
                            onClick={(e) => handleAddValue(e, index)}
                            disabled={formState.isLoading}
                          >
                            Add
                          </button>
                        </div>

                        <button
                          type="button"
                          className="al-btn al-btn--sm al-btn--danger"
                          onClick={() => handleRemoveRow(index)}
                          disabled={
                            formState.isLoading ||
                            formState.extraFields.length <= 1
                          }
                        >
                          Remove
                        </button>
                      </div>

                      {field.values.length > 0 && (
                        <div className="al-fieldrow__chips">
                          {field.values.map((val, valIndex) => (
                            <div key={valIndex} className="al-chip">
                              {val}
                              <button
                                type="button"
                                onClick={() =>
                                  handleRemoveValue(index, valIndex)
                                }
                                className="al-chip__x"
                                disabled={formState.isLoading}
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Description Field */}
                <div className="mb-4">
                  <label className="al-label">
                    Specifications
                  </label>

                  <CKEditor
                    editor={ClassicEditor}
                    data={EditProduct.values.description}
                    className="custom-ckeditor"
                    onChange={(event, editor) => {
                      const data = editor.getData();
                      EditProduct.setFieldValue("description", data);
                    }}
                    onBlur={() =>
                      EditProduct.setFieldTouched("description", true)
                    }
                    disabled={formState.isLoading}
                  />
                </div>

                {/* FAQ Section */}
                <div className="al-subpanel">
                  <div className="al-subpanel__head">
                    <span className="al-subpanel__title">Frequently Asked Questions</span>
                    <button
                      type="button"
                      className="al-btn al-btn--sm"
                      onClick={addFaq}
                      disabled={formState.isLoading}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" /></svg>
                      Add FAQ
                    </button>
                  </div>

                  {formState.faqs.map((faq, index) => (
                    <div className="row mb-3" key={index}>
                      <div className="col-md-5 mb-2">
                        <input
                          type="text"
                          className="al-input"
                          placeholder="Question"
                          value={faq.question}
                          onChange={(e) =>
                            handleFaqChange(index, "question", e.target.value)
                          }
                          disabled={formState.isLoading}
                        />
                      </div>
                      <div className="col-md-5 mb-2">
                        <input
                          type="text"
                          className="al-input"
                          placeholder="Answer"
                          value={faq.answer}
                          onChange={(e) =>
                            handleFaqChange(index, "answer", e.target.value)
                          }
                          disabled={formState.isLoading}
                        />
                      </div>
                      <div className="col-md-2 mb-2 d-flex align-items-center">
                        <button
                          type="button"
                          className="al-btn al-btn--sm al-btn--danger"
                          onClick={() => removeFaq(index)}
                          disabled={
                            formState.faqs.length <= 1 || formState.isLoading
                          }
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Add Option Modal */}
              <AddOptionModal
                show={formState.showAddOptionModal}
                onClose={() => updateFormState({ showAddOptionModal: false })}
                onAddOption={(newOption) => {
                  handleAddOption(formState.addOptionModalField, newOption);
                }}
              />

              <div className="al-actionbar">
                <button type="button" className="al-btn" onClick={() => Navigate(-1)} disabled={formState.isLoading}>Cancel</button>
                <button
                  type="submit"
                  className="al-btn al-btn--primary"
                  disabled={EditProduct.isSubmitting || formState.isLoading}
                >
                  {formState.isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Updating Product...
                    </>
                  ) : (
                    <>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                      Update Product
                    </>
                  )}
                </button>
              </div>
            </form>
      </div>
    </div>
  );
};

export default ProductEdit;
